import { useMemo } from "react";
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { motion } from "framer-motion";
import { useTenantTranslation } from "hooks/formatters";
import { CrewComparisonData } from "src/store/api/generatedApi";
import {
  MEDAL_COLORS,
  DEFAULT_MEDAL_COLOR,
} from "components/common/plots/colorSets";
import { useFormatNumber } from "src/utils/formatNumber";

type CrewLeaderboardProps = Readonly<{
  data: CrewComparisonData;
}>;

const PODIUM_HEIGHTS = {
  1: 60,
  2: 45,
  3: 35,
} as const;

type PodiumPositionProps = Readonly<{
  crewId: string;
  rank: 1 | 2 | 3;
  strikesPerHeat: number;
  crewLabel: string;
}>;

const PODIUM_GROW_DURATION = 0.3;
const POPIN_DELAY = 0.2;

const PodiumPosition = ({
  crewId,
  rank,
  strikesPerHeat,
  crewLabel,
}: PodiumPositionProps) => {
  const formatNumber = useFormatNumber();
  const medalColor = MEDAL_COLORS[rank] ?? DEFAULT_MEDAL_COLOR;
  const podiumHeight = PODIUM_HEIGHTS[rank];

  const baseDelay = 0.15 * (rank - 1);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        alignSelf: "flex-end",
      }}
    >
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: baseDelay + POPIN_DELAY,
          duration: 0.3,
          type: "spring",
          stiffness: 400,
          damping: 15,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: medalColor,
            color: "white",
            mb: 1,
          }}
        >
          <EmojiEventsIcon sx={{ fontSize: 24 }} />
        </Box>
      </motion.div>

      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: baseDelay + POPIN_DELAY,
          duration: 0.3,
          type: "spring",
          stiffness: 400,
          damping: 15,
        }}
      >
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontWeight: 700, mb: 0.5, textAlign: "center" }}
        >
          {crewLabel} {crewId}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            mb: 1,
            textAlign: "center",
          }}
        >
          {formatNumber(2, true)(strikesPerHeat)}
        </Typography>
      </motion.div>

      <motion.div
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{
          delay: baseDelay,
          duration: PODIUM_GROW_DURATION,
          ease: [0.34, 1.56, 0.64, 1],
        }}
        style={{ originY: 1 }}
      >
        <Box
          sx={{
            width: 100,
            height: podiumHeight + 8,
            bgcolor: medalColor,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: "white",
            }}
          >
            {rank}
          </Typography>
        </Box>
      </motion.div>
    </Box>
  );
};

export const CrewLeaderboard = ({ data }: CrewLeaderboardProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const formatNumber = useFormatNumber();

  const rankedCrews = useMemo(() => {
    return [...data.crews]
      .filter((c) => c.crew_id !== "All")
      .sort((a, b) => a.mean_strikes_per_heat - b.mean_strikes_per_heat);
  }, [data.crews]);

  const topThree = rankedCrews.slice(0, 3);
  const rest = rankedCrews.slice(3);

  const podiumOrder = [topThree[1], topThree[0], topThree[2]].filter(
    (crew): crew is (typeof topThree)[number] => crew !== undefined
  );

  return (
    <Stack>
      <Paper elevation={0} sx={{ p: 2, overflowX: "auto" }}>
        <Stack direction="row" spacing={4} alignItems="flex-end" mb={-2}>
          {podiumOrder.length > 0 && (
            <Stack direction="row" spacing={0.5} alignItems="flex-end">
              {podiumOrder.map((crew) => {
                const rank = (rankedCrews.indexOf(crew) + 1) as 1 | 2 | 3;
                return (
                  <PodiumPosition
                    key={crew.crew_id + rank.toString()}
                    crewId={crew.crew_id}
                    rank={rank}
                    strikesPerHeat={crew.mean_strikes_per_heat}
                    crewLabel={t("crew")}
                  />
                );
              })}
            </Stack>
          )}

          {rest.length > 0 && (
            <Stack
              direction="row"
              spacing={3}
              alignItems="flex-end"
              sx={{ ml: 2, pb: 2 }}
            >
              {rest.map((crew, index) => {
                const rank = index + 4;
                // start off at half the height of bronze, and decrease each step
                const step = PODIUM_HEIGHTS[3] / (rest.length * 2);
                const heightOffset = (rest.length - 1 - index) * step;
                // Start after podium animations complete, then stagger
                const restDelay = 0.15 * 2 + PODIUM_GROW_DURATION + 0.1 * index;
                return (
                  <motion.div
                    key={crew.crew_id + rank.toString()}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{
                      delay: restDelay,
                      duration: 0.3,
                      type: "spring",
                      stiffness: 400,
                      damping: 15,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        pb: `${heightOffset}px`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: theme.palette.text.secondary,
                          color: theme.palette.background.paper,
                          fontWeight: 700,
                          fontSize: "1rem",
                          mb: 1,
                        }}
                      >
                        {rank}
                      </Box>

                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      >
                        {t("crew")} {crew.crew_id}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      >
                        {formatNumber(2, true)(crew.mean_strikes_per_heat)}
                      </Typography>
                    </Box>
                  </motion.div>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Paper>
      <Stack direction="row" alignItems="center" spacing={2} ml={2}>
        <Typography variant="body2" color="text.secondary">
          {t("meanStrikesPerHeat")}
        </Typography>
      </Stack>
    </Stack>
  );
};

const SkeletonPodiumPosition = ({ rank }: { rank: 1 | 2 | 3 }) => {
  const podiumHeight = PODIUM_HEIGHTS[rank] + 8;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        alignSelf: "flex-end",
      }}
    >
      <Skeleton variant="circular" width={44} height={44} sx={{ mb: 1 }} />
      <Skeleton variant="text" width={60} height={24} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width={30} height={24} sx={{ mb: 0.5 }} />
      <Skeleton
        variant="rounded"
        width={100}
        height={podiumHeight}
        sx={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
      />
    </Box>
  );
};

export const SkeletonCrewLeaderboard = () => {
  return (
    <Stack>
      <Paper elevation={0} sx={{ p: 2, overflowX: "auto" }}>
        <Stack direction="row" spacing={4} alignItems="flex-end" mb={-2}>
          <Stack direction="row" spacing={0.5} alignItems="flex-end">
            <SkeletonPodiumPosition rank={2} />
            <SkeletonPodiumPosition rank={1} />
            <SkeletonPodiumPosition rank={3} />
          </Stack>
        </Stack>
      </Paper>
      <Skeleton variant="text" width={100} height={16} sx={{ ml: 2 }} />
    </Stack>
  );
};
